/**
 * ACTIVITY-CORE (L1) — IN-MEMORY undo stack for the session.
 *
 * UX complement to the history model: each user action records its
 * INVERSE mutation (applied through PowerSync's normal write path, never by
 * rewinding state). It clears on reload/account switch — durable undo is
 * the feed revert (server-side, `reverted`).
 */

export interface UndoableAction {
  /** Short text for the toast/UI ("Task deleted", …). Already localized. */
  label: string;
  /** Applies the inverse mutation (normal write path). */
  undo: () => Promise<void> | void;
}

export interface UndoStack {
  /**
   * Pushes an action. Returns a handle that REMOVES it from the stack if still
   * present (true if it removed it): the toast uses this to consume "its" action
   * without clobbering more recent ones (and so Ctrl+Z does not repeat it).
   */
  push(action: UndoableAction): () => boolean;
  /**
   * Undoes the last action (LIFO). Returns the undone action or null if the
   * stack is empty. If `undo()` throws, the action is NOT re-enqueued (avoids loops).
   */
  undoLast(): Promise<UndoableAction | null>;
  /** Last action without undoing it (for displaying the toast). */
  peek(): UndoableAction | null;
  clear(): void;
  readonly size: number;
}

const DEFAULT_LIMIT = 50;

export function createUndoStack(limit: number = DEFAULT_LIMIT): UndoStack {
  const stack: UndoableAction[] = [];

  return {
    push(action: UndoableAction) {
      stack.push(action);
      if (stack.length > limit) {
        stack.splice(0, stack.length - limit);
      }
      return () => {
        const index = stack.indexOf(action);
        if (index === -1) {
          return false;
        }
        stack.splice(index, 1);
        return true;
      };
    },
    async undoLast() {
      const action = stack.pop();
      if (!action) {
        return null;
      }
      await action.undo();
      return action;
    },
    peek() {
      return stack.at(-1) ?? null;
    },
    clear() {
      stack.length = 0;
    },
    get size() {
      return stack.length;
    },
  };
}
